import React, { useState } from 'react';
import { FileDown, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface DownloadFileCardProps {
  args: {
    fileId: string;
    fileName: string;
    sizeBytes?: number;
    title?: string;
    reportTitle?: string;
  };
  buttonSize: string;
}

const formatSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * A file Arie generated in chat (create_report_pdf). Informational — nothing
 * to confirm. The file endpoint needs the bearer token, so both buttons fetch
 * it as a blob rather than linking to the URL directly.
 */
const DownloadFileCard: React.FC<DownloadFileCardProps> = ({ args, buttonSize }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState<'download' | 'open' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchBlob = async (): Promise<Blob> => {
    const res = await fetch(`${API_BASE_URL}/api/assistant/files/${args.fileId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(res.status === 404 ? 'This file is no longer available.' : 'Could not load the file.');
    return res.blob();
  };

  const handleDownload = async () => {
    setBusy('download');
    setError(null);
    try {
      const url = URL.createObjectURL(await fetchBlob());
      const link = document.createElement('a');
      link.href = url;
      link.download = args.fileName || 'summary.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setBusy(null);
    }
  };

  const handleOpen = async () => {
    // Open the tab synchronously (popup blockers), then point it at the blob.
    const win = window.open('', '_blank');
    setBusy('open');
    setError(null);
    try {
      const url = URL.createObjectURL(await fetchBlob());
      if (win) win.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      win?.close();
      setError(err instanceof Error ? err.message : 'Could not open the file.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-md bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
          <FileDown className="w-5 h-5 text-red-600 dark:text-red-400" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-gray-800 dark:text-gray-100 break-words">{args.fileName}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PDF{args.sizeBytes ? ` · ${formatSize(args.sizeBytes)}` : ''}{args.reportTitle ? ` · from "${args.reportTitle}"` : ''}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleDownload}
          disabled={!!busy}
          className={`${buttonSize} inline-flex items-center gap-1.5 bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
        >
          {busy === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          Download PDF
        </button>
        <button
          onClick={handleOpen}
          disabled={!!busy}
          className={`${buttonSize} inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60`}
        >
          {busy === 'open' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Open
        </button>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default DownloadFileCard;
